import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import uuid4 from 'uuid/v4';
import { normalizeMediaObject } from 'api';
import { defaultMapStyle } from 'app-constants';
import { useAnalytics } from 'hooks';
import { Route, Switch } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import { PinMap } from 'msp-components';
import Error404 from 'components/Error404';

const normalizeMarkerTypeData = items => items.map(item => ({
  id: item.uid,
  label: item.name,
  icon: item.icon,
  color: undefined,
  size: item.size,
  inLegend: item.inLegend,
}));

const normalizeCategoryData = items => items.map(item => ({
  id: uuid4(),
  label: item,
}));

const normalizeMapItemData = (items, imgWidth = 1500, categories) => items.reduce((result, item) => {
  const media = item.media.map(media => normalizeMediaObject(media, imgWidth));
  const categoryIds = item.categories.map(c => categories.find(({ label }) => label === c).id);

  const itemObj = {
    id: item.uid,
    slug: item.slug,
    title: item.name,
    description: item.description,
    locationType: item.locationType,
    latitude: item.latitude,
    longitude: item.longitude,
    countries: item.countries,
    markerTypeId: item.marker,
    categoryIds,
    media,
  };

  result.byId[item.uid] = itemObj;
  result.bySlug[item.slug] = itemObj;
  result.ordered.push(itemObj);
  return result;
}, { bySlug: {}, byId: {}, ordered: [] });

const PinMapTitle = ({
  routeProps,
  mapStyle,
  mapboxToken,
  displayOptions,
  mapItems,
  markerTypes,
  categories,
}) => {
  const [containerWidth, setContainerWidth] = useState();
  const handleResize = width => setContainerWidth(width);

  const { match, history } = routeProps;

  const { itemsList, legend } = displayOptions.menuItems;

  const mapTitleCategories = useMemo(() => normalizeCategoryData(categories), [categories]);
  const mapTitleMarkerTypes = useMemo(() => normalizeMarkerTypeData(markerTypes), [markerTypes]);
  const mapTitleItems = useMemo(() => (
    normalizeMapItemData(mapItems, containerWidth && containerWidth * 1.5, mapTitleCategories)
  ), [mapItems, containerWidth, mapTitleCategories]);

  const handleItemSelect = id => {
    if (id) {
      const slug = mapTitleItems.byId[id].slug;
      history.push(`${match.url}/${slug}`);
    } else {
      history.push(match.url);
    }
  };

  const { trackMediaPlay, trackMediaProgress } = useAnalytics();

  return (
    <Switch>
      <Route
        exact
        path={`${match.url}/:itemSlug?`}
        render={props => {
          // Validate slug
          let activeItemId = null;
          // eslint-disable-next-line
          const { itemSlug } = props.match.params;
          if (itemSlug) {
            const activeItem = mapTitleItems.bySlug[itemSlug];
            if (typeof activeItem !== 'undefined') {
              activeItemId = activeItem.id;
            } else {
              return <Error404 />;
            }
          }

          return (
            <>
              <ResizeDetector
                handleWidth
                onResize={handleResize}
                refreshMode="debounce"
                refreshRate={500}
              />
              <PinMap
                mapboxToken={mapboxToken}
                mapStyle={mapStyle || defaultMapStyle}
                hideLegend={!legend}
                hideItemsList={!itemsList}
                markerTypes={mapTitleMarkerTypes}
                categories={mapTitleCategories}
                items={mapTitleItems.ordered}
                activeItemId={activeItemId}
                onSelectItem={handleItemSelect}
                onMediaPlay={trackMediaPlay}
                onMediaProgress={trackMediaProgress}
              />
            </>
          );
        }}
      />
      <Route component={Error404} />
    </Switch>
  );
};

PinMapTitle.propTypes = {
  routeProps: PropTypes.object,
  mapStyle: PropTypes.string,
  mapboxToken: PropTypes.string,
  displayOptions: PropTypes.shape({
    menuItems: PropTypes.shape({
      legend: PropTypes.bool,
      itemsList: PropTypes.bool,
    }),
  }),
  mapItems: PropTypes.arrayOf(PropTypes.object),
  markerTypes: PropTypes.arrayOf(PropTypes.object),
  categories: PropTypes.arrayOf(PropTypes.string),
};

export default PinMapTitle;
